import { useEffect, useMemo, useState } from "react";
import Layout from "@/components/layout/Layout";
import {
  listWorkOrders,
  listClients,
  listSites,
  listAssets,
  createWorkOrder,
  aiTriage,
} from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, Filter, Search, Sparkles, LayoutGrid, List, Download } from "lucide-react";
import { StatusPill, TypePill, PriorityPill } from "@/components/Pills";
import { toast } from "sonner";
import { exportWorkOrdersCsvUrl } from "@/lib/api";

const COLS = [
  { key: "open", label: "Open" },
  { key: "in_progress", label: "In Progress" },
  { key: "on_hold", label: "On Hold" },
  { key: "completed", label: "Completed" },
];

export default function WorkOrders() {
  const { user, settings } = useAuth();
  const [wos, setWos] = useState([]);
  const [q, setQ] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [view, setView] = useState("board");
  const [open, setOpen] = useState(false);

  const load = async () => {
    const data = await listWorkOrders();
    setWos(data);
  };
  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    return wos.filter((w) => {
      if (filterType !== "all" && w.type !== filterType) return false;
      if (q && !`${w.number} ${w.title}`.toLowerCase().includes(q.toLowerCase())) return false;
      return true;
    });
  }, [wos, q, filterType]);

  const byStatus = useMemo(() => {
    const m = {};
    COLS.forEach((c) => (m[c.key] = []));
    filtered.forEach((w) => {
      if (m[w.status]) m[w.status].push(w);
    });
    return m;
  }, [filtered]);

  const canCreate = user?.role === "admin" || user?.role === "client";

  return (
    <Layout
      title="Work Orders"
      breadcrumbs={["Operations", "Work Orders"]}
      actions={
        <div className="flex items-center gap-2">
          <a
            data-testid="export-csv-btn"
            href={exportWorkOrdersCsvUrl()}
            className="inline-flex items-center gap-1 h-9 px-3 rounded-sm border border-zinc-300 bg-white hover:bg-zinc-50 text-xs font-bold uppercase tracking-wider"
          >
            <Download className="h-3.5 w-3.5" /> CSV
          </a>
          {canCreate && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button
                  data-testid="new-wo-button"
                  className="rounded-sm bg-orange-500 hover:bg-orange-600 text-white font-semibold"
                >
                  <Plus className="h-4 w-4 mr-1" /> New
                </Button>
              </DialogTrigger>
              <NewWoDialog
                onCreated={() => {
                  setOpen(false);
                  load();
                }}
                aiEnabled={settings?.ai_enabled}
              />
            </Dialog>
          )}
        </div>
      }
    >
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
          <Input
            data-testid="wo-search"
            placeholder="Search by number or title…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9 h-10 rounded-sm bg-white border-zinc-300"
          />
        </div>
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger
            data-testid="wo-type-filter"
            className="w-full sm:w-44 h-10 rounded-sm bg-white border-zinc-300"
          >
            <Filter className="h-4 w-4 mr-1" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="PM">Preventive (PM)</SelectItem>
            <SelectItem value="CM">Corrective (CM)</SelectItem>
          </SelectContent>
        </Select>
        <Tabs value={view} onValueChange={setView} className="shrink-0">
          <TabsList className="rounded-sm bg-zinc-100 p-1 h-10">
            <TabsTrigger value="board" data-testid="view-board" className="rounded-sm">
              <LayoutGrid className="h-4 w-4 mr-1" /> Board
            </TabsTrigger>
            <TabsTrigger value="list" data-testid="view-list" className="rounded-sm">
              <List className="h-4 w-4 mr-1" /> List
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {view === "board" ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          {COLS.map((c) => (
            <div key={c.key} className="bg-zinc-100 rounded-sm border border-zinc-200">
              <div className="flex items-center justify-between px-3 py-2 border-b border-zinc-200">
                <div className="flex items-center gap-2">
                  <StatusPill status={c.key} />
                </div>
                <span className="font-mono text-xs font-bold text-zinc-600">
                  {byStatus[c.key]?.length || 0}
                </span>
              </div>
              <div className="p-2 space-y-2 max-h-[70vh] overflow-y-auto">
                {(byStatus[c.key] || []).map((w) => (
                  <WoCard key={w.id} w={w} />
                ))}
                {(byStatus[c.key] || []).length === 0 && (
                  <div className="text-xs text-zinc-400 px-2 py-3 text-center">No items</div>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="engineered-card overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-zinc-50 border-b border-zinc-200">
              <tr className="text-left">
                <Th>Number</Th>
                <Th>Title</Th>
                <Th>Type</Th>
                <Th>Priority</Th>
                <Th>Status</Th>
                <Th>Due</Th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((w) => (
                <tr key={w.id} className="border-b border-zinc-100 hover:bg-zinc-50">
                  <td className="px-4 py-3 font-mono font-bold">
                    <Link to={`/work-orders/${w.id}`} data-testid={`row-${w.number}`} className="hover:text-orange-600">
                      {w.number}
                    </Link>
                  </td>
                  <td className="px-4 py-3">{w.title}</td>
                  <td className="px-4 py-3"><TypePill type={w.type} /></td>
                  <td className="px-4 py-3"><PriorityPill priority={w.priority} /></td>
                  <td className="px-4 py-3"><StatusPill status={w.status} /></td>
                  <td className="px-4 py-3 font-mono text-xs text-zinc-600">
                    {w.due_date ? new Date(w.due_date).toLocaleDateString() : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filtered.length === 0 && (
            <div className="p-8 text-center text-sm text-zinc-500">No work orders found.</div>
          )}
        </div>
      )}
    </Layout>
  );
}

const Th = ({ children }) => (
  <th className="px-4 py-2 text-[10px] font-bold tracking-[0.15em] uppercase text-zinc-500">
    {children}
  </th>
);

const WoCard = ({ w }) => (
  <Link
    to={`/work-orders/${w.id}`}
    data-testid={`board-card-${w.number}`}
    className="block bg-white border border-zinc-200 hover:border-orange-500 transition-colors rounded-sm p-3"
  >
    <div className="flex items-start justify-between">
      <span className="font-mono text-xs font-bold text-zinc-950">{w.number}</span>
      <PriorityPill priority={w.priority} />
    </div>
    <div className="font-semibold text-sm text-zinc-950 mt-2 line-clamp-2">{w.title}</div>
    <div className="flex items-center justify-between mt-3">
      <TypePill type={w.type} />
      <span className="text-[10px] font-mono text-zinc-500">
        {w.due_date ? new Date(w.due_date).toLocaleDateString() : ""}
      </span>
    </div>
  </Link>
);

function NewWoDialog({ onCreated, aiEnabled }) {
  const [type, setType] = useState("CM");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState("medium");
  const [clientId, setClientId] = useState("");
  const [siteId, setSiteId] = useState("");
  const [assetId, setAssetId] = useState("");
  const [clients, setClients] = useState([]);
  const [sites, setSites] = useState([]);
  const [assets, setAssets] = useState([]);
  const [busy, setBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiOut, setAiOut] = useState("");

  useEffect(() => {
    listClients().then(setClients);
  }, []);
  useEffect(() => {
    if (clientId) {
      listSites(clientId).then(setSites);
      setSiteId("");
      setAssetId("");
    }
  }, [clientId]);
  useEffect(() => {
    if (siteId) {
      listAssets({ site_id: siteId }).then(setAssets);
      setAssetId("");
    }
  }, [siteId]);

  const runTriage = async () => {
    setAiBusy(true);
    setAiOut("");
    try {
      const a = assets.find((x) => x.id === assetId);
      const res = await aiTriage({
        title,
        description,
        asset_category: a?.category,
        asset_name: a?.name,
      });
      setAiOut(res.triage);
    } catch (e) {
      toast.error("AI failed");
    } finally {
      setAiBusy(false);
    }
  };

  const submit = async () => {
    if (!title || !clientId || !siteId) {
      toast.error("Title, client and site are required");
      return;
    }
    setBusy(true);
    try {
      await createWorkOrder({
        type,
        title,
        description,
        priority,
        client_id: clientId,
        site_id: siteId,
        asset_id: assetId || null,
        sla_hours: priority === "critical" ? 4 : priority === "high" ? 8 : 24,
      });
      toast.success("Work order created");
      onCreated();
    } catch (e) {
      toast.error("Failed to create");
    } finally {
      setBusy(false);
    }
  };

  return (
    <DialogContent className="max-w-2xl rounded-sm">
      <DialogHeader>
        <DialogTitle className="font-display text-2xl">
          New Work Order{" "}
          <span className="font-mono text-base text-zinc-500">
            ({type === "PM" ? "PM-####" : "CM-####"} auto)
          </span>
        </DialogTitle>
      </DialogHeader>
      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2 grid grid-cols-2 gap-2">
          <button
            type="button"
            data-testid="wo-type-pm"
            onClick={() => setType("PM")}
            className={`p-3 rounded-sm border text-left transition-colors ${
              type === "PM"
                ? "border-emerald-500 bg-emerald-50"
                : "border-zinc-200 hover:border-zinc-400"
            }`}
          >
            <div className="font-mono text-xs font-bold text-emerald-700">PM-####</div>
            <div className="font-bold mt-1">Preventive</div>
            <div className="text-xs text-zinc-600">Scheduled, planned maintenance</div>
          </button>
          <button
            type="button"
            data-testid="wo-type-cm"
            onClick={() => setType("CM")}
            className={`p-3 rounded-sm border text-left transition-colors ${
              type === "CM"
                ? "border-rose-500 bg-rose-50"
                : "border-zinc-200 hover:border-zinc-400"
            }`}
          >
            <div className="font-mono text-xs font-bold text-rose-700">CM-####</div>
            <div className="font-bold mt-1">Corrective</div>
            <div className="text-xs text-zinc-600">Reactive repair, breakdown</div>
          </button>
        </div>
        <div className="col-span-2">
          <Label className="text-xs font-semibold uppercase tracking-wider">Title</Label>
          <Input
            data-testid="wo-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Chiller leaking refrigerant"
            className="rounded-sm h-11"
          />
        </div>
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider">Client</Label>
          <Select value={clientId} onValueChange={setClientId}>
            <SelectTrigger data-testid="wo-client" className="rounded-sm h-11">
              <SelectValue placeholder="Select client" />
            </SelectTrigger>
            <SelectContent>
              {clients.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider">Site</Label>
          <Select value={siteId} onValueChange={setSiteId} disabled={!clientId}>
            <SelectTrigger data-testid="wo-site" className="rounded-sm h-11">
              <SelectValue placeholder="Select site" />
            </SelectTrigger>
            <SelectContent>
              {sites.map((s) => (
                <SelectItem key={s.id} value={s.id}>
                  {s.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider">Asset (optional)</Label>
          <Select value={assetId} onValueChange={setAssetId} disabled={!siteId}>
            <SelectTrigger data-testid="wo-asset" className="rounded-sm h-11">
              <SelectValue placeholder="Select asset" />
            </SelectTrigger>
            <SelectContent>
              {assets.map((a) => (
                <SelectItem key={a.id} value={a.id}>
                  {a.tag} · {a.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider">Priority</Label>
          <Select value={priority} onValueChange={setPriority}>
            <SelectTrigger data-testid="wo-priority" className="rounded-sm h-11">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="low">Low</SelectItem>
              <SelectItem value="medium">Medium</SelectItem>
              <SelectItem value="high">High</SelectItem>
              <SelectItem value="critical">Critical</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="col-span-2">
          <Label className="text-xs font-semibold uppercase tracking-wider">Description</Label>
          <Textarea
            data-testid="wo-desc"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={4}
            className="rounded-sm"
            placeholder="What happened? Smell, noise, vibration, fault codes…"
          />
        </div>
        {aiEnabled && (
          <div className="col-span-2 border border-orange-200 bg-orange-50 rounded-sm p-3">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2 text-orange-800 font-bold text-sm">
                <Sparkles className="h-4 w-4" /> AI Triage Suggestion
              </div>
              <Button
                size="sm"
                variant="outline"
                data-testid="ai-triage-btn"
                onClick={runTriage}
                disabled={!title || aiBusy}
                className="rounded-sm border-orange-400 text-orange-800 hover:bg-orange-100"
              >
                {aiBusy ? "Analyzing…" : "Analyze"}
              </Button>
            </div>
            {aiOut ? (
              <pre className="text-xs whitespace-pre-wrap font-mono text-zinc-800 leading-relaxed max-h-40 overflow-y-auto" data-testid="ai-triage-output">
                {aiOut}
              </pre>
            ) : (
              <div className="text-xs text-orange-700/80">
                Fill title (and asset) then click <b>Analyze</b> for likely causes, urgency, parts, and safety checks.
              </div>
            )}
          </div>
        )}
      </div>
      <DialogFooter>
        <Button
          data-testid="wo-submit"
          disabled={busy}
          onClick={submit}
          className="bg-orange-500 hover:bg-orange-600 rounded-sm font-bold"
        >
          {busy ? "Creating…" : `Create ${type === "PM" ? "PM" : "CM"} Work Order`}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}
